import { computed, watch, shallowRef } from 'vue'
import { useCountdown } from '@vueuse/core'

import type { ComputedRef, ShallowRef, MaybeRefOrGetter } from 'vue'

import dayjs from 'dayjs'

import { getClockNumber } from '@shared/lib/format'

import { useCurrentTime } from './current-time.hook'

interface TimeCountdownPayload {
  date: ComputedRef<string | null>
}

interface TimeCountdownReturn {
  countdown: ShallowRef<number>
  remaining: ShallowRef<number>
  days: ComputedRef<number>
  hours: ComputedRef<number>
  minutes: ComputedRef<number>
  seconds: ComputedRef<number>
  hoursText: ComputedRef<string>
  minutesText: ComputedRef<string>
  secondsText: ComputedRef<string>
  countdownText: ComputedRef<string>
  start: (countdown?: MaybeRefOrGetter<number>) => void
}

type TimeCountdownFormat = 'compact' | 'full'

interface TimeCountdownOptions {
  format?: TimeCountdownFormat
  onStart?: (diffSeconds: number) => void
  onComplete?: (diffSeconds: number) => void
}

export const useTimeCountdown = (payload: TimeCountdownPayload, options?: TimeCountdownOptions): TimeCountdownReturn => {
  const {
    currentTime,
  } = useCurrentTime()

  const diffSeconds = computed<number>(() => {
    const valid = typeof payload.date.value === 'string'
    if (!valid) {
      return 0
    } else {
      const now = dayjs(currentTime.value)
      const target = dayjs(payload.date.value)
      const diff = target.diff(now, 'second')
      return Math.max(0, diff)
    }
  })

  const countdown = shallowRef<number>(diffSeconds.value)

  const { start, remaining } = useCountdown(countdown, {
    onComplete: () => {
      options?.onComplete?.(diffSeconds.value)
    },
  })

  watch(diffSeconds, (sec) => {
    countdown.value = sec
    options?.onStart?.(diffSeconds.value)
    start(sec)
  }, { immediate: true })

  const days = computed(() => Math.floor(remaining.value / 86400))
  const hours = computed(() => Math.floor((remaining.value % 86400) / 3600))
  const minutes = computed(() => Math.floor((remaining.value % 3600) / 60))
  const seconds = computed(() => Math.floor(remaining.value % 60))

  const hoursText = computed(() => getClockNumber(hours.value) || '00')
  const minutesText = computed(() => getClockNumber(minutes.value) || '00')
  const secondsText = computed(() => getClockNumber(seconds.value) || '00')

  const isFullFormat = options?.format === 'full'

  const countdownText = computed(() => {
    const d = days.value > 0 ? `${days.value}d` : ''
    const h = (isFullFormat || hours.value > 0) ? `${hoursText.value}:` : ''
    return `${d} ${h}${minutesText.value}:${secondsText.value}`.trim()
  })

  return {
    countdown,
    remaining,
    days,
    hours,
    minutes,
    seconds,
    hoursText,
    minutesText,
    secondsText,
    countdownText,
    start,
  }
}
